import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, useReducedMotion } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const SECOND = 1000;

function pad(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

/**
 * Chic ticking countdown to kick-off, shown only for upcoming matches. Picks the
 * three most meaningful units (days drop off once we are inside 24h so seconds
 * can tick), each in its own glass tile.
 */
export function KickoffCountdown({ kickoffAt }: { kickoffAt: string }) {
  const { t } = useTranslation();
  const { arabicNumerals } = useLocale();
  const reduce = useReducedMotion();
  const target = useMemo(() => new Date(kickoffAt).getTime(), [kickoffAt]);
  const [remaining, setRemaining] = useState(() => Math.max(0, target - Date.now()));

  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, target - Date.now()));
    tick();
    const timer = setInterval(tick, SECOND);
    return () => clearInterval(timer);
  }, [target]);

  if (remaining <= 0) return null;

  const totalSec = Math.floor(remaining / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const segments =
    days > 0
      ? [
          { value: days, label: t('match.unitDays') },
          { value: hours, label: t('match.unitHours') },
          { value: mins, label: t('match.unitMins') },
        ]
      : [
          { value: hours, label: t('match.unitHours') },
          { value: mins, label: t('match.unitMins') },
          { value: secs, label: t('match.unitSecs') },
        ];

  return (
    <Animated.View entering={reduce ? undefined : FadeIn.duration(400)}>
      <Card className="mx-4 mb-4 items-center gap-3 overflow-hidden px-4 py-5">
        <View className="flex-row items-center gap-2">
          <View className="h-1.5 w-1.5 rounded-full bg-volt" />
          <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
            {t('match.kicksOffIn')}
          </AppText>
        </View>
        <View className="flex-row items-stretch gap-2">
          {segments.map((seg, index) => (
            <React.Fragment key={seg.label}>
              {index > 0 ? (
                <AppText variant="title" weight="bold" className="self-center text-muted">
                  :
                </AppText>
              ) : null}
              <View className="min-w-[60px] items-center rounded-2xl border border-line bg-inset px-3 py-2.5">
                <AppText variant="title" weight="bold" tabular className="text-volt-bright">
                  {localizeDigits(pad(seg.value), arabicNumerals)}
                </AppText>
                <AppText
                  variant="micro"
                  weight="medium"
                  className="mt-0.5 uppercase tracking-wide text-muted"
                >
                  {seg.label}
                </AppText>
              </View>
            </React.Fragment>
          ))}
        </View>
      </Card>
    </Animated.View>
  );
}
